import type { Address, PatchUser, PaymentMethod, PutDefaultAddress, User } from '@repo/api-types'
import { queryOptions } from '@tanstack/vue-query'
import { useClient } from '@/composables/useClient'
import { entityKeys } from '@/queries/entity-keys'
import { HttpError } from '@/queries/auth-error'
import { mutationOptions } from '@/queries/mutation-options'

const accountKeys = {
  profile: () => [...entityKeys.account(), 'profile'] as const,
  defaultAddress: () => [...entityKeys.address()] as const,
  defaultPaymentMethod: () => [...entityKeys.payment()] as const,
  emailTaken: (email: string) => [...entityKeys.account(), 'email-taken', email] as const,
}

export const accountQueries = {
  profile: () =>
    queryOptions({
      queryKey: accountKeys.profile(),
      queryFn: async (): Promise<User> => {
        const client = await useClient()
        const { status, body } = await client.auth.getMe()

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to fetch user profile')
      },
    }),

  defaultAddress: () =>
    queryOptions({
      queryKey: accountKeys.defaultAddress(),
      queryFn: async (): Promise<Address> => {
        const client = await useClient()
        const { status, body } = await client.auth.getDefaultCustomerAddress()

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to fetch default address')
      },
    }),

  defaultPaymentMethod: () =>
    queryOptions({
      queryKey: accountKeys.defaultPaymentMethod(),
      queryFn: async (): Promise<PaymentMethod> => {
        const client = await useClient()
        const { status, body } = await client.payment.getDefaultPaymentMethod()

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to fetch default payment method')
      },
    }),

  emailTaken: (email: string) =>
    queryOptions({
      queryKey: accountKeys.emailTaken(email),
      queryFn: async (): Promise<{ isTaken: boolean }> => {
        const client = await useClient()
        const { status, body } = await client.auth.isEmailTaken({ query: { email } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to check email availability')
      },
    }),
}

export const accountMutations = {
  updateProfile: () =>
    mutationOptions({
      mutationFn: async (user: PatchUser): Promise<User> => {
        const client = await useClient()
        const { status, body } = await client.auth.patchMe({ body: user })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update account details')
      },
      invalidation: { awaited: [{ entity: 'account' }] },
    }),

  updatePassword: () =>
    mutationOptions({
      mutationFn: async ({ newPassword }: { newPassword: string }): Promise<{ success: boolean }> => {
        const client = await useClient()
        const { status, body } = await client.auth.updatePassword({ body: { newPassword } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update password')
      },
      invalidation: 'skip',
      // EditAccountPassword.vue renders its own LCallout with support contact details,
      // so the global toast would be redundant.
      onError: (error) => {
        console.error(error)
      },
    }),

  updateDefaultAddress: () =>
    mutationOptions({
      mutationFn: async (address: PutDefaultAddress): Promise<{ success: boolean }> => {
        const client = await useClient()
        const { status, body } = await client.auth.updateDefaultCustomerAddress({ body: address })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update default address')
      },
      invalidation: { awaited: [{ entity: 'address' }] },
    }),

  updateDefaultPaymentMethod: () =>
    mutationOptions({
      mutationFn: async ({
        paymentMethodId,
        provider,
      }: {
        paymentMethodId: string
        provider: string
      }): Promise<PaymentMethod> => {
        const client = await useClient()
        const { status, body } = await client.payment.updateDefaultPaymentMethod({
          body: { paymentMethodId, provider },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update payment method')
      },
      invalidation: { awaited: [{ entity: 'payment' }] },
    }),
}
